// src/ParticipantProfile.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Chip,
  Tabs,
  Tab,
  MenuItem,
  IconButton,
  Grid,
  Tooltip,
  Snackbar,
  Alert,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import { serverTimestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { getAllItems, updateItem } from './firestore';

const PrimaryButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 800,
  boxShadow: 'none',
  backgroundColor: '#000',
  color: '#fff',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#222',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  },
  '&:disabled': { backgroundColor: '#111', color: 'rgba(255,255,255,0.6)' },
});

const SubtleButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 800,
  boxShadow: 'none',
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0,0,0,0.15)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#fafafa',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
  },
});

const SoftCard = ({ title, icon, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 4,
      border: '1px solid rgba(0,0,0,0.08)',
      bgcolor: 'white',
      boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5, flexWrap: 'wrap' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 999,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(0,0,0,0.06)',
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>
        {title}
      </Typography>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {children}
  </Paper>
);

const STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

// ✅ consistent white dropdown menus
const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: '#ffffff',
      color: '#000000',
      '& .MuiMenuItem-root': {
        color: '#000000',
        '&:hover': { backgroundColor: '#f5f5f5' },
        '&.Mui-selected': { backgroundColor: '#e0e0e0', '&:hover': { backgroundColor: '#d0d0d0' } },
      },
    },
  },
};

// Premium selectable card used for billing options
const BillingCard = ({ title, subtitle, selected, onClick }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      p: 2.2,
      borderRadius: 4,
      cursor: 'pointer',
      border: selected ? '2px solid #000' : '1px solid rgba(0,0,0,0.10)',
      bgcolor: selected ? 'rgba(0,0,0,0.04)' : 'white',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        borderColor: '#000',
        boxShadow: '0 18px 40px rgba(0,0,0,0.08)',
      },
      userSelect: 'none',
      height: '100%',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Chip
        size="small"
        label={selected ? 'Selected' : ' '}
        sx={{
          height: 22,
          opacity: selected ? 1 : 0,
          borderRadius: 999,
          bgcolor: '#000',
          color: 'white',
          fontWeight: 800,
        }}
      />
      <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
      {subtitle}
    </Typography>
  </Paper>
);

const ParticipantProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [tab, setTab] = useState(0);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const participant = useMemo(() => participants.find((p) => p.id === id), [participants, id]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const load = async () => {
      try {
        const loaded = await getAllItems(currentUser.uid, 'participants');
        setParticipants(loaded || []);
      } catch (e) {
        console.error(e);
        showToast('error', 'Failed to load participant.');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!participant) return;

    const safeParticipant = {
      ...participant,
      planStart: participant.planStart || '',
      planEnd: participant.planEnd || '',
      goals: Array.isArray(participant.goals) ? participant.goals : [],
      addressLine1: participant.addressLine1 || '',
      addressLine2: participant.addressLine2 || '',
      suburb: participant.suburb || '',
      state: participant.state || '',
      postcode: participant.postcode || '',
      billingPreference: participant.billingPreference || 'plan-managed',
      contacts: {
        planManagerName: participant.contacts?.planManagerName || '',
        planManagerEmail: participant.contacts?.planManagerEmail || '',
        planManagerPhone: participant.contacts?.planManagerPhone || '',
        supportCoordinatorName: participant.contacts?.supportCoordinatorName || '',
        supportCoordinatorEmail: participant.contacts?.supportCoordinatorEmail || '',
        supportCoordinatorPhone: participant.contacts?.supportCoordinatorPhone || '',
        guardianName: participant.contacts?.guardianName || '',
        guardianEmail: participant.contacts?.guardianEmail || '',
        guardianPhone: participant.contacts?.guardianPhone || '',
        emergencyContacts: Array.isArray(participant.contacts?.emergencyContacts)
          ? participant.contacts.emergencyContacts
          : [],
      },
      _newGoal: '',
    };

    setForm(safeParticipant);
    setDirty(false);
  }, [participant]);

  const setField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) };
        cur = cur[k];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  const addGoal = (text) => {
    const t = (text || '').trim();
    if (!t) return;
    setField('goals', [...(form?.goals || []), t]);
  };

  const removeGoal = (idx) => {
    const next = (form?.goals || []).filter((_, i) => i !== idx);
    setField('goals', next);
  };

  const addEmergencyContact = () => {
    const next = [
      ...(form?.contacts?.emergencyContacts || []),
      { name: '', phone: '', relationship: '' },
    ];
    setField('contacts.emergencyContacts', next);
  };

  const removeEmergencyContact = (idx) => {
    const next = (form?.contacts?.emergencyContacts || []).filter((_, i) => i !== idx);
    setField('contacts.emergencyContacts', next);
  };

  const save = async () => {
    if (!form) return;

    const cleanName = (form.name || '').trim();
    const cleanNdis = (form.ndisNumber || '').replace(/\D/g, '').slice(0, 9);

    if (!cleanName) return showToast('warning', 'Participant name is required.');
    if (!/^\d{9}$/.test(cleanNdis)) return showToast('warning', 'NDIS Number must be exactly 9 digits.');

    setSaving(true);
    try {
      const payload = {
        name: cleanName,
        ndisNumber: cleanNdis,

        planStart: form.planStart || '',
        planEnd: form.planEnd || '',
        goals: Array.isArray(form.goals) ? form.goals.map((g) => String(g).trim()).filter(Boolean) : [],

        addressLine1: form.addressLine1 || '',
        addressLine2: form.addressLine2 || '',
        suburb: form.suburb || '',
        state: form.state || '',
        postcode: (form.postcode || '').replace(/\D/g, '').slice(0, 4),

        billingPreference: form.billingPreference || 'plan-managed',

        contacts: {
          planManagerName: form.contacts?.planManagerName || '',
          planManagerEmail: form.contacts?.planManagerEmail || '',
          planManagerPhone: form.contacts?.planManagerPhone || '',
          supportCoordinatorName: form.contacts?.supportCoordinatorName || '',
          supportCoordinatorEmail: form.contacts?.supportCoordinatorEmail || '',
          supportCoordinatorPhone: form.contacts?.supportCoordinatorPhone || '',
          guardianName: form.contacts?.guardianName || '',
          guardianEmail: form.contacts?.guardianEmail || '',
          guardianPhone: form.contacts?.guardianPhone || '',
          emergencyContacts: Array.isArray(form.contacts?.emergencyContacts)
            ? form.contacts.emergencyContacts.map((c) => ({
                name: (c.name || '').trim(),
                phone: (c.phone || '').trim(),
                relationship: (c.relationship || '').trim(),
              }))
            : [],
        },

        updatedAt: serverTimestamp(),
      };

      await updateItem(currentUser.uid, 'participants', id, payload);
      setParticipants((prev) => prev.map((p) => (p.id === id ? { ...p, ...payload } : p)));

      setDirty(false);
      showToast('success', 'Participant profile saved.');
    } catch (e) {
      console.error(e);
      showToast('error', `Failed to save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const billingLabel =
    form?.billingPreference === 'ndia'
      ? 'NDIA-managed'
      : form?.billingPreference === 'self-managed'
      ? 'Self-managed'
      : 'Plan-managed';

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      showToast('success', `${label} copied.`);
    } catch {
      showToast('error', `Could not copy ${label}.`);
    }
  };

  if (loading || !form) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading profile...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Premium Header */}
      <Box
        sx={{
          mb: 2.5,
          p: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(245,247,255,1) 45%, rgba(245,255,248,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SubtleButton onClick={() => navigate('/participants')} startIcon={<ArrowBackIcon />}>
            Back
          </SubtleButton>

          <Box sx={{ minWidth: 240, flex: 1 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1 }}
            >
              {form.name || 'Participant'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0.8 }}>
              <Chip
                size="small"
                label={`NDIS: ${form.ndisNumber || '—'}`}
                sx={{ borderRadius: 999, fontWeight: 800, bgcolor: 'rgba(0,0,0,0.06)' }}
              />
              <Chip
                size="small"
                label={billingLabel}
                sx={{
                  borderRadius: 999,
                  fontWeight: 800,
                  bgcolor: 'rgba(0,0,0,0.06)',
                  color: 'rgba(0,0,0,0.75)',
                }}
              />
              {dirty && (
                <Chip
                  size="small"
                  label="Unsaved changes"
                  color="warning"
                  sx={{ fontWeight: 800 }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title="Copy NDIS number">
              <span>
                <SubtleButton
                  onClick={() => copyToClipboard(form.ndisNumber, 'NDIS number')}
                  startIcon={<ContentCopyIcon />}
                >
                  Copy NDIS
                </SubtleButton>
              </span>
            </Tooltip>

            <PrimaryButton
              startIcon={<SaveIcon />}
              onClick={save}
              disabled={saving || !dirty}
            >
              {saving ? 'Saving…' : 'Save'}
            </PrimaryButton>
          </Box>
        </Box>
      </Box>

      {/* Tabs container */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: 'white',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            bgcolor: 'rgba(0,0,0,0.02)',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 900 },
          }}
        >
          <Tab icon={<PersonOutlineIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<AssignmentOutlinedIcon />} iconPosition="start" label="Plan" />
          <Tab icon={<PaymentsOutlinedIcon />} iconPosition="start" label="Billing" />
          <Tab icon={<FlagOutlinedIcon />} iconPosition="start" label="Goals" />
          <Tab icon={<ContactPhoneOutlinedIcon />} iconPosition="start" label="Contacts" />
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          {/* OVERVIEW */}
          {tab === 0 && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <SoftCard title="Participant details" icon={<PersonOutlineIcon fontSize="small" />}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <TextField
                    label="Participant Name"
                    value={form.name || ''}
                    onChange={(e) => setField('name', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 360 } }}
                  />
                  <TextField
                    label="NDIS Number (9 digits)"
                    value={form.ndisNumber || ''}
                    onChange={(e) =>
                      setField('ndisNumber', e.target.value.replace(/\D/g, '').slice(0, 9))
                    }
                    sx={{ width: { xs: '100%', sm: 260 } }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Copy NDIS number">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => copyToClipboard(form.ndisNumber, 'NDIS number')}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </SoftCard>

              <SoftCard title="Address" icon={<HomeOutlinedIcon fontSize="small" />}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <TextField
                    label="Address line 1"
                    value={form.addressLine1 || ''}
                    onChange={(e) => setField('addressLine1', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 460 } }}
                  />
                  <TextField
                    label="Address line 2"
                    value={form.addressLine2 || ''}
                    onChange={(e) => setField('addressLine2', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 360 } }}
                  />
                  <TextField
                    label="Suburb"
                    value={form.suburb || ''}
                    onChange={(e) => setField('suburb', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 260 } }}
                  />
                  <TextField
                    select
                    label="State"
                    value={form.state || ''}
                    onChange={(e) => setField('state', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 140 } }}
                    SelectProps={{ MenuProps: menuProps }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {STATES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Postcode"
                    value={form.postcode || ''}
                    onChange={(e) => setField('postcode', e.target.value.replace(/\D/g, '').slice(0, 4))}
                    sx={{ width: { xs: '100%', sm: 160 } }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                </Box>
              </SoftCard>
            </Box>
          )}

          {/* PLAN */}
          {tab === 1 && (
            <SoftCard title="Plan details" icon={<AssignmentOutlinedIcon fontSize="small" />}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="Plan start date"
                  type="date"
                  value={form.planStart || ''}
                  onChange={(e) => setField('planStart', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: '100%', sm: 260 } }}
                />
                <TextField
                  label="Plan end date"
                  type="date"
                  value={form.planEnd || ''}
                  onChange={(e) => setField('planEnd', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: '100%', sm: 260 } }}
                />

                <Box sx={{ width: '100%' }} />

                <Chip
                  label={
                    form.planStart && form.planEnd
                      ? `Plan: ${form.planStart} → ${form.planEnd}`
                      : 'Add plan dates for quick reference'
                  }
                  sx={{ borderRadius: 999, fontWeight: 800, bgcolor: 'rgba(0,0,0,0.06)' }}
                />
              </Box>
            </SoftCard>
          )}

          {/* BILLING */}
          {tab === 2 && (
            <SoftCard title="Billing preference" icon={<PaymentsOutlinedIcon fontSize="small" />}>
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mb: 2 }}>
                Choose how this participant typically manages funding. This can drive future automation
                (invoice defaults, who to email, payment instructions).
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <BillingCard
                    title="NDIA-managed"
                    subtitle="Invoices are typically paid via NDIA processes (more strict references/formatting)."
                    selected={form.billingPreference === 'ndia'}
                    onClick={() => setField('billingPreference', 'ndia')}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <BillingCard
                    title="Plan-managed"
                    subtitle="Most common. Usually billed to a plan manager (store their contact details in Contacts)."
                    selected={form.billingPreference === 'plan-managed'}
                    onClick={() => setField('billingPreference', 'plan-managed')}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <BillingCard
                    title="Self-managed"
                    subtitle="Participant (or nominee) pays directly. Clear payment instructions help reduce delays."
                    selected={form.billingPreference === 'self-managed'}
                    onClick={() => setField('billingPreference', 'self-managed')}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Selected: ${billingLabel}`}
                  sx={{ borderRadius: 999, fontWeight: 800, bgcolor: 'rgba(0,0,0,0.06)' }}
                />
              </Box>
            </SoftCard>
          )}

          {/* GOALS */}
          {tab === 3 && (
            <SoftCard title="Goals" icon={<FlagOutlinedIcon fontSize="small" />}>
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mb: 2 }}>
                Keep goals short. These can later power smart suggestions (shift notes, progress summaries).
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Add a goal"
                  value={form._newGoal || ''}
                  onChange={(e) => setField('_newGoal', e.target.value)}
                  sx={{ width: { xs: '100%', sm: 560 } }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addGoal(form._newGoal);
                      setField('_newGoal', '');
                    }
                  }}
                />
                <PrimaryButton
                  startIcon={<AddIcon />}
                  onClick={() => {
                    addGoal(form._newGoal);
                    setField('_newGoal', '');
                  }}
                >
                  Add
                </PrimaryButton>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(form.goals || []).length === 0 ? (
                  <Chip label="No goals yet" sx={{ borderRadius: 999 }} />
                ) : (
                  (form.goals || []).map((g, idx) => (
                    <Chip
                      key={`${g}-${idx}`}
                      label={g}
                      onDelete={() => removeGoal(idx)}
                      sx={{ borderRadius: 999, fontWeight: 700 }}
                    />
                  ))
                )}
              </Box>
            </SoftCard>
          )}

          {/* CONTACTS */}
          {tab === 4 && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <SoftCard title="Plan manager" icon={<ContactPhoneOutlinedIcon fontSize="small" />}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <TextField
                    label="Name"
                    value={form.contacts.planManagerName}
                    onChange={(e) => setField('contacts.planManagerName', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 260 } }}
                  />
                  <TextField
                    label="Email"
                    value={form.contacts.planManagerEmail}
                    onChange={(e) => setField('contacts.planManagerEmail', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 280 } }}
                  />
                  <TextField
                    label="Phone"
                    value={form.contacts.planManagerPhone}
                    onChange={(e) => setField('contacts.planManagerPhone', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  />
                </Box>
              </SoftCard>

              <SoftCard title="Support coordinator" icon={<ContactPhoneOutlinedIcon fontSize="small" />}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <TextField
                    label="Name"
                    value={form.contacts.supportCoordinatorName}
                    onChange={(e) => setField('contacts.supportCoordinatorName', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 260 } }}
                  />
                  <TextField
                    label="Email"
                    value={form.contacts.supportCoordinatorEmail}
                    onChange={(e) => setField('contacts.supportCoordinatorEmail', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 280 } }}
                  />
                  <TextField
                    label="Phone"
                    value={form.contacts.supportCoordinatorPhone}
                    onChange={(e) => setField('contacts.supportCoordinatorPhone', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  />
                </Box>
              </SoftCard>

              <SoftCard title="Guardian / nominee" icon={<ContactPhoneOutlinedIcon fontSize="small" />}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <TextField
                    label="Name"
                    value={form.contacts.guardianName}
                    onChange={(e) => setField('contacts.guardianName', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 260 } }}
                  />
                  <TextField
                    label="Email"
                    value={form.contacts.guardianEmail}
                    onChange={(e) => setField('contacts.guardianEmail', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 280 } }}
                  />
                  <TextField
                    label="Phone"
                    value={form.contacts.guardianPhone}
                    onChange={(e) => setField('contacts.guardianPhone', e.target.value)}
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  />
                </Box>
              </SoftCard>

              <SoftCard title="Emergency contacts" icon={<ContactPhoneOutlinedIcon fontSize="small" />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                    Add contacts for urgent situations.
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <PrimaryButton startIcon={<AddIcon />} onClick={addEmergencyContact}>
                    Add contact
                  </PrimaryButton>
                </Box>

                {(form.contacts.emergencyContacts || []).length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                    No emergency contacts added.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(form.contacts.emergencyContacts || []).map((c, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: 'rgba(0,0,0,0.02)',
                          border: '1px solid rgba(0,0,0,0.08)',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                          <TextField
                            label="Name"
                            value={c.name || ''}
                            onChange={(e) => {
                              const next = [...(form.contacts.emergencyContacts || [])];
                              next[idx] = { ...next[idx], name: e.target.value };
                              setField('contacts.emergencyContacts', next);
                            }}
                            sx={{ width: { xs: '100%', sm: 260 } }}
                          />
                          <TextField
                            label="Phone"
                            value={c.phone || ''}
                            onChange={(e) => {
                              const next = [...(form.contacts.emergencyContacts || [])];
                              next[idx] = { ...next[idx], phone: e.target.value };
                              setField('contacts.emergencyContacts', next);
                            }}
                            sx={{ width: { xs: '100%', sm: 220 } }}
                          />
                          <TextField
                            label="Relationship"
                            value={c.relationship || ''}
                            onChange={(e) => {
                              const next = [...(form.contacts.emergencyContacts || [])];
                              next[idx] = { ...next[idx], relationship: e.target.value };
                              setField('contacts.emergencyContacts', next);
                            }}
                            sx={{ width: { xs: '100%', sm: 240 } }}
                          />

                          <Box sx={{ flex: 1 }} />

                          <Tooltip title="Remove">
                            <IconButton onClick={() => removeEmergencyContact(idx)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </SoftCard>
            </Box>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ParticipantProfile;


